import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { getSidebarRoutes, isRouteActive } from '../config/routeConfig';
import { userProfileStore } from '../stateManagers/stores/userProfileStore';

interface SidebarProps {
  currentPath: string;
  onNavigate: (routeId: string, params?: Record<string, string>, data?: Record<string, any>) => void;
  onLogout: () => void;
}

// ── Inline SVG icons (no icon-font dependency) ───────────────────────────────
const Ico = ({ d, size = 15.37, style = {} }: { d: string | string[]; size?: number; style?: React.CSSProperties }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...style }}
  >
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
type IcoProps = { size?: number; style?: React.CSSProperties };
const IcoSearch = (p: IcoProps) => <Ico {...p} d={['M11 19a8 8 0 100-16 8 8 0 000 16z', 'M21 21l-4.3-4.3']} />;
const IcoGrid = (p: IcoProps) => <Ico {...p} d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />;
const IcoChevron = (p: IcoProps) => <Ico {...p} d="M6 9l6 6 6-6" />;
const IcoCollapse = (p: IcoProps) => <Ico {...p} d={['M11 17l-5-5 5-5', 'M18 17l-5-5 5-5']} />;
const IcoExpand = (p: IcoProps) => <Ico {...p} d={['M13 17l5-5-5-5', 'M6 17l5-5-5-5']} />;
const IcoSignOut = (p: IcoProps) => <Ico {...p} d={['M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4', 'M16 17l5-5-5-5', 'M21 12H9']} />;

// ── Route icon resolver: maps route id/label/pi-class → SVG ──────────────────
const ICON_PATHS: Record<string, string | string[]> = {
  home: 'M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z',
  users: ['M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M9 7a4 4 0 100 8 4 4 0 000-8z', 'M23 21v-2a4 4 0 00-3-3.87', 'M16 3.13a4 4 0 010 7.75'],
  user: ['M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2', 'M12 11a4 4 0 100-8 4 4 0 000 8z'],
  grad: ['M22 10v6M2 10l10-5 10 5-10 5z', 'M6 12v5c3 3 9 3 12 0v-5'],
  upload: ['M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4', 'M17 8l-5-5-5 5', 'M12 3v12'],
  target: ['M12 22a10 10 0 100-20 10 10 0 000 20z', 'M12 18a6 6 0 100-12 6 6 0 000 12z', 'M12 14a2 2 0 100-4 2 2 0 000 4z'],
  briefcase: ['M3 7h18v13H3z', 'M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2'],
  cog: ['M12 15a3 3 0 100-6 3 3 0 000 6z', 'M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z'],
  calendar: ['M8 2v4M16 2v4M3 10h18', 'M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z'],
  chart: ['M18 20V10', 'M12 20V4', 'M6 20v-6'],
  wallet: ['M21 12V7H5a2 2 0 010-4h14v4', 'M3 5v14a2 2 0 002 2h16v-5', 'M18 12a2 2 0 100 4 2 2 0 000-4z'],
  mail: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6'],
  book: ['M4 19.5A2.5 2.5 0 016.5 17H20', 'M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z'],
  check: ['M22 11.08V12a10 10 0 11-5.93-9.14', 'M22 4L12 14.01l-3-3'],
  bell: ['M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 01-3.46 0'],
  file: ['M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z', 'M14 2v6h6', 'M9 13h6M9 17h6'],
  dot: ['M12 16a4 4 0 100-8 4 4 0 000 8z'],
  sheets: [
    'M4 5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2z',
    'M4 9h16',
    'M4 15h16',
    'M10 9v12',
  ],
  // broadcast/beacon: centre dot + 2 signal arcs on each side
  beacon: [
    'M12 14a2 2 0 100-4 2 2 0 000 4z',
    'M16.24 7.76a6 6 0 010 8.49',
    'M7.76 16.24a6 6 0 010-8.49',
    'M19.07 4.93a10 10 0 010 14.14',
    'M4.93 19.07a10 10 0 010-14.14',
  ],
};

// NOTE: order matters — first match wins. Beacon must stay above sheets.
const RULES: [RegExp, string][] = [
  [/beacon|broadcast|wifi|radio/, 'beacon'],
  [/sheet|excel|spreadsheet|csv/, 'sheets'],
  [/import|upload/, 'upload'],
  [/lead/, 'target'],
  [/staff|employee|team/, 'briefcase'],
  [/student/, 'grad'],
  [/teacher|users|parent/, 'users'],
  [/dashboard|home|th-large/, 'home'],
  [/setup|setting|cog/, 'cog'],
  [/fee|payment|wallet|money|dollar|invoice/, 'wallet'],
  [/exam|calendar|schedule|event|timetable/, 'calendar'],
  [/report|chart|analytic/, 'chart'],
  [/mail|message|inbox/, 'mail'],
  [/course|class|book|subject/, 'book'],
  [/attendance|check/, 'check'],
  [/notice|bell|notif/, 'bell'],
  [/document|file|letter/, 'file'],
  [/user|profile/, 'user'],
];

const RouteIcon = ({ id, label, icon, size = 15.37 }: { id?: string; label?: string; icon?: string; size?: number }) => {
  const text = `${label || ''} ${id || ''} ${icon || ''}`.toLowerCase();
  const key = RULES.find(([re]) => re.test(text))?.[1] || 'dot';
  return <Ico d={ICON_PATHS[key]} size={size} />;
};

// ── Tokens ───────────────────────────────────────────────────────────────────
const T = {
  bg1: '#0f4a43',
  bg2: '#0c3d37',
  bg3: '#0a322d',
  lime: '#c8e94b',
  white: '#ffffff',
  text: 'rgba(255,255,255,0.78)',
  muted: 'rgba(255,255,255,0.5)',
};
const EXPANDED_W = 245;
const COLLAPSED_W = 78;
const HIDE_DELAY = 180;
const FLOAT_LEFT = COLLAPSED_W + 9.2; // flyout + tooltip offset

const getInitials = (name?: string | null) => {
  if (!name) return 'U';
  const p = name.trim().split(' ');
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
};

// ── Hover-with-delay hook (for collapsed flyouts) ────────────────────────────
function useHoverPanel(hideDelay = HIDE_DELAY) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enter = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setOpen(true);
  }, []);
  const leave = useCallback(() => {
    timer.current = setTimeout(() => setOpen(false), hideDelay);
  }, [hideDelay]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return { open, setOpen, enter, leave };
}

// ── Collapsed group: icon + hover flyout with children ───────────────────────
interface CollapsedGroupProps {
  route: any;
  children: any[];
  isActive: boolean;
  currentPath: string;
  onOpen: () => void;
  onNavigate: (id: string) => void;
}

const CollapsedGroup: React.FC<CollapsedGroupProps> = ({ route, children, isActive, currentPath, onOpen, onNavigate }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(0);
  const hover = useHoverPanel();

  const handleEnter = () => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      const panelH = children.length * 39 + 48;
      setTop(Math.max(8, Math.min(r.top, window.innerHeight - panelH - 8)));
    }
    hover.enter();
  };

  return (
    <div style={{ marginBottom: 4.1 }}>
      <div ref={ref} onMouseEnter={handleEnter} onMouseLeave={hover.leave}>
        <button
          className={`sb-btn ${isActive ? 'sb-active' : ''}`}
          style={{ justifyContent: 'center', padding: '11.27px 0' }}
          onClick={() => {
            hover.setOpen(false);
            onOpen();
          }}
        >
          <RouteIcon id={route.id} label={route.label} icon={route.icon} size={16.4} />
        </button>
      </div>

      {hover.open && (
        <div
          className="sb-flyout"
          style={{ top, left: FLOAT_LEFT }}
          onMouseEnter={hover.enter}
          onMouseLeave={hover.leave}
        >
          <div className="sb-flyout-title">{route.label}</div>
          {children.map(({ id: childId, route: cr }: any) => (
            <button
              key={childId}
              className={`sb-flyout-item ${isRouteActive(childId, currentPath) ? 'sb-flyout-active' : ''}`}
              onClick={() => {
                hover.setOpen(false);
                onNavigate(childId);
              }}
            >
              <RouteIcon id={childId} label={cr.label} icon={cr.icon} size={14.35} />
              {cr.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main Sidebar ─────────────────────────────────────────────────────────────
const Sidebar: React.FC<SidebarProps> = ({ currentPath, onNavigate, onLogout }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['schoolSetup']));
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [submenuHeights, setSubmenuHeights] = useState<Record<string, number>>({});
  const [tip, setTip] = useState<{ label: string; top: number } | null>(null);
  const [clickedItem, setClickedItem] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const submenuRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const searchRef = useRef<HTMLInputElement>(null);

  const profile = userProfileStore((s: any) => s.profile);
  const role = profile?.role;

  const sidebarRoutes = useMemo(() => getSidebarRoutes(undefined, role), [role]);

  const visibleRoutes = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sidebarRoutes;
    return sidebarRoutes
      .map((r: any) => {
        if (r.route.label.toLowerCase().includes(q)) return r;
        const kids = (r.children || []).filter((c: any) => c.route.label.toLowerCase().includes(q));
        return kids.length ? { ...r, children: kids } : null;
      })
      .filter(Boolean) as typeof sidebarRoutes;
  }, [sidebarRoutes, query]);

  useEffect(() => {
    const heights: Record<string, number> = {};
    Object.entries(submenuRefs.current).forEach(([id, ref]) => {
      if (ref) heights[id] = ref.scrollHeight;
    });
    setSubmenuHeights(heights);
  }, []);

  useEffect(() => {
    const newExpanded = new Set<string>();
    sidebarRoutes.forEach(({ id, children }) => {
      if (children && isRouteActive(id, currentPath)) newExpanded.add(id);
    });
    const cur = Array.from(expandedItems).sort();
    const nxt = Array.from(newExpanded).sort();
    if (cur.length !== nxt.length || cur.some((item, i) => item !== nxt[i])) setExpandedItems(newExpanded);
  }, [currentPath]);

  useEffect(() => {
    if (isCollapsed) setExpandedItems(new Set());
    setTip(null);
  }, [isCollapsed]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsCollapsed(false);
        setTimeout(() => searchRef.current?.focus(), 320);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleExpanded = (routeId: string) => {
    const next = new Set(expandedItems);
    next.has(routeId) ? next.delete(routeId) : next.add(routeId);
    setExpandedItems(next);
  };

  // Tooltip: position measured from the hovered element (collapsed mode only)
  const showTip = (label: string, e: React.MouseEvent<HTMLElement>) => {
    if (!isCollapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ label, top: r.top + r.height / 2 });
  };
  const hideTip = () => setTip(null);

  const handleNavigate = (routeId: string) => {
    setTip(null);
    setClickedItem(routeId);
    setTimeout(() => setClickedItem(null), 300);
    setTimeout(() => onNavigate(routeId, {}, {}), 150);
  };

  const W = isCollapsed ? COLLAPSED_W : EXPANDED_W;

  return (
    <>
      <style>{CSS}</style>

      <div
        className="sb-root"
        style={{
          width: W,
          minWidth: W,
          background: `linear-gradient(180deg, ${T.bg1} 0%, ${T.bg2} 55%, ${T.bg3} 100%)`,
          padding: isCollapsed ? '18.45px 11.27px' : '18.45px 14.35px',
          color: T.white,
          height: '100vh',
          alignSelf: 'flex-start',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1), min-width 0.3s cubic-bezier(0.4,0,0.2,1)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxSizing: 'border-box',
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            display: 'flex',
            flexDirection: isCollapsed ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 13.32,
            paddingBottom: 16.4,
            borderBottom: '1px dashed rgba(255,255,255,0.12)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 9.22, overflow: 'hidden' }}>
            <div
              style={{
                width: 32.8,
                height: 32.8,
                background: T.lime,
                borderRadius: 9.22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: T.bg1,
              }}
            >
              <IcoGrid size={15.37} />
            </div>
            {!isCollapsed && (
              <h2 className="sb-label" style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, letterSpacing: '-0.3px', whiteSpace: 'nowrap' }}>
                PitchOut
              </h2>
            )}
          </div>
          <button
            className="sb-icon-btn"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <IcoExpand size={14.35} /> : <IcoCollapse size={14.35} />}
          </button>
        </div>

        {/* ── Search ── */}
        <div style={{ marginTop: 16.4 }}>
          {isCollapsed ? (
            <button
              className="sb-icon-btn"
              style={{ width: '100%', height: 38.95, borderRadius: 9.22 }}
              onClick={() => {
                setIsCollapsed(false);
                setTimeout(() => searchRef.current?.focus(), 320);
              }}
              onMouseEnter={e => showTip('Search', e)}
              onMouseLeave={hideTip}
            >
              <IcoSearch size={14.35} />
            </button>
          ) : (
            <div className="sb-search">
              <IcoSearch size={14.35} style={{ color: T.text }} />
              <input ref={searchRef} value={query} onChange={e => setQuery(e.target.value)} placeholder="Search" />
              <span className="sb-kbd">⌘ K</span>
            </div>
          )}
        </div>

        {/* ── Nav ── */}
        <nav style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', marginTop: 2 }}>
          {!isCollapsed ? (
            <div style={{ fontSize: '0.74rem', fontWeight: 600, margin: '16.4px 4.1px 9.22px' }}>Main Menu</div>
          ) : (
            <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: '13.32px 5.12px' }} />
          )}

          {visibleRoutes.length === 0 && !isCollapsed && (
            <div style={{ padding: '7.17px 5.12px', fontSize: '0.79rem', color: T.muted }}>No results</div>
          )}

          {visibleRoutes.map(({ id, route, children }: any) => {
            const isActive = isRouteActive(id, currentPath);
            const hasChildren = !!children && children.length > 0;
            const isExpanded = expandedItems.has(id) || (!!query && hasChildren);
            const highlight = isActive && !hasChildren;

            // Collapsed + group → flyout
            if (isCollapsed && hasChildren) {
              return (
                <CollapsedGroup
                  key={id}
                  route={route}
                  children={children}
                  isActive={isActive}
                  currentPath={currentPath}
                  onOpen={() => {
                    setIsCollapsed(false);
                    setExpandedItems(new Set([id]));
                  }}
                  onNavigate={handleNavigate}
                />
              );
            }

            return (
              <div key={id} style={{ marginBottom: 4.1 }}>
                <button
                  className={`sb-btn ${highlight ? 'sb-active' : ''}`}
                  onClick={() => (hasChildren ? toggleExpanded(id) : handleNavigate(id))}
                  onMouseEnter={e => showTip(route.label, e)}
                  onMouseLeave={hideTip}
                  style={{
                    padding: isCollapsed ? '11.27px 0' : '10.25px 11.27px',
                    justifyContent: isCollapsed ? 'center' : hasChildren ? 'space-between' : 'flex-start',
                    transform: clickedItem === id ? 'scale(0.97)' : 'scale(1)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11.27 }}>
                    <RouteIcon id={id} label={route.label} icon={route.icon} size={16.4} />
                    {!isCollapsed && <span className="sb-label">{route.label}</span>}
                  </div>
                  {hasChildren && !isCollapsed && (
                    <IcoChevron
                      size={13.32}
                      style={{
                        transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      }}
                    />
                  )}
                  {highlight && <span className="sb-bar" />}
                </button>

                {hasChildren && !isCollapsed && (
                  <div
                    ref={el => {
                      submenuRefs.current[id] = el;
                    }}
                    style={{
                      marginLeft: 18.45,
                      borderLeft: '1px solid rgba(255,255,255,0.12)',
                      paddingLeft: 7.17,
                      maxHeight: isExpanded ? `${submenuHeights[id] || 1000}px` : '0px',
                      opacity: isExpanded ? 1 : 0,
                      overflow: 'hidden',
                      transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
                      marginTop: isExpanded ? 4.1 : 0,
                    }}
                  >
                    {children.map(({ id: childId, route: cr }: any) => {
                      const childActive = isRouteActive(childId, currentPath);
                      return (
                        <button
                          key={childId}
                          className={`sb-btn sb-sub ${childActive ? 'sb-active' : ''}`}
                          onClick={() => handleNavigate(childId)}
                          style={{ transform: clickedItem === childId ? 'scale(0.97)' : 'scale(1)' }}
                        >
                          <RouteIcon id={childId} label={cr.label} icon={cr.icon} size={14.35} />
                          <span>{cr.label}</span>
                          {childActive && <span className="sb-bar" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* ── Account ── */}
        <div style={{ paddingTop: 13.32, borderTop: '1px dashed rgba(255,255,255,0.12)' }}>
          {isCollapsed ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9.22 }}>
              <div
                className="sb-avatar"
                style={{ width: 34.85, height: 34.85, cursor: 'pointer' }}
                onClick={() => onNavigate('profile')}
              >
                {getInitials(profile?.name)}
              </div>
              <button
                className="sb-icon-btn sb-logout"
                onClick={onLogout}
                onMouseEnter={e => showTip('Logout', e)}
                onMouseLeave={hideTip}
                aria-label="Logout"
                style={{ width: 38.95, height: 38.95, borderRadius: 9.22 }}
              >
                <IcoSignOut size={15.37} />
              </button>
            </div>
          ) : (
            <div className="sb-user" onClick={() => onNavigate('profile')} style={{ cursor: 'pointer' }}>
              <div className="sb-avatar" style={{ width: 32.8, height: 32.8 }}>{getInitials(profile?.name)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.81rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {profile?.name || 'User'}
                </div>
                {profile?.role && (
                  <div style={{ fontSize: '0.67rem', color: T.muted, textTransform: 'capitalize', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {profile.role}
                  </div>
                )}
              </div>
              <button
                className="sb-icon-btn sb-logout"
                onClick={(e) => {
                  e.stopPropagation();
                  onLogout();
                }}
                aria-label="Logout"
                title="Logout"
                style={{ border: 'none', background: 'transparent' }}
              >
                <IcoSignOut size={15.37} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Tooltip (fixed, escapes overflow) ── */}
      {isCollapsed && tip && (
        <div className="sb-tip" style={{ top: tip.top, left: FLOAT_LEFT }}>
          {tip.label}
        </div>
      )}
    </>
  );
};

// ── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
@keyframes sb-fadeIn { from { opacity:0; transform:translateX(-4px); } to { opacity:1; transform:translateX(0); } }
@keyframes sb-popIn  { from { opacity:0; transform:translateX(-8px) scale(0.96); } to { opacity:1; transform:translateX(0) scale(1); } }

.sb-label { animation: sb-fadeIn 0.2s ease both; }

.sb-btn {
  position: relative;
  width: 100%;
  padding: 10.25px 11.27px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 9.22px;
  color: rgba(255,255,255,0.78);
  cursor: pointer;
  text-align: left;
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 11.27px;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s, border-color 0.2s, transform 0.2s;
}
.sb-btn:hover:not(.sb-active) { background: rgba(255,255,255,0.06); color: #fff; }
.sb-btn.sb-active {
  background: rgba(255,255,255,0.09);
  border-color: rgba(255,255,255,0.07);
  color: #c8e94b;
}
.sb-sub { padding: 9.22px 11.27px; margin-bottom: 3.07px; font-size: 0.81rem; border-radius: 8.2px; }
.sb-bar {
  position: absolute; right: 0; top: 22%; bottom: 22%; width: 3px;
  border-radius: 3px 0 0 3px; background: #c8e94b;
}

.sb-icon-btn {
  width: 31.77px; height: 31.77px; border-radius: 8.2px; flex-shrink: 0;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.78);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s, color 0.2s;
}
.sb-icon-btn:hover { background: rgba(255,255,255,0.12); color: #fff; }
.sb-logout:hover { background: rgba(239,68,68,0.18); color: #fca5a5; }

.sb-search {
  display: flex; align-items: center; gap: 9.22px;
  padding: 0 9.22px 0 11.27px; height: 38.95px; border-radius: 9.22px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  transition: border-color 0.2s, background 0.2s;
}
.sb-search:focus-within { border-color: rgba(200,233,75,0.55); background: rgba(255,255,255,0.09); }
.sb-search input {
  flex: 1; min-width: 0; background: transparent; border: none; outline: none;
  color: #fff; font-size: 0.81rem;
}
.sb-search input::placeholder { color: rgba(255,255,255,0.6); }
.sb-kbd {
  font-size: 0.65rem; font-weight: 600; color: rgba(255,255,255,0.78);
  padding: 3.07px 6.15px; border-radius: 5.12px; white-space: nowrap;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
}

.sb-user {
  display: flex; align-items: center; gap: 9.22px; padding: 7.17px; border-radius: 11.27px;
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
}
.sb-avatar {
  border-radius: 9.22px; background: #c8e94b; color: #0f4a43;
  font-weight: 700; font-size: 0.76rem; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}

/* Collapsed hover flyout (left is set inline from COLLAPSED_W) */
.sb-flyout {
  position: fixed; z-index: 9999; min-width: 202.95px;
  background: linear-gradient(160deg, #0f4a43 0%, #0a322d 100%);
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: 13.32px; padding: 7.17px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.35), 0 2px 10px rgba(0,0,0,0.2);
  animation: sb-popIn 0.18s ease both;
}
.sb-flyout-title {
  font-size: 0.67rem; font-weight: 700; letter-spacing: 0.6px;
  color: rgba(255,255,255,0.45); padding: 4.1px 9.22px 7.17px;
  border-bottom: 1px solid rgba(255,255,255,0.1); margin-bottom: 4.1px;
}
.sb-flyout-item {
  width: 100%; padding: 8.2px 11.27px; margin-bottom: 2.05px;
  background: transparent; border: none; border-radius: 8.2px;
  color: rgba(255,255,255,0.85); cursor: pointer; text-align: left;
  font-size: 0.81rem; font-weight: 500;
  display: flex; align-items: center; gap: 9.22px; white-space: nowrap;
  transition: background 0.15s, color 0.15s;
}
.sb-flyout-item:hover { background: rgba(255,255,255,0.1); color: #fff; }
.sb-flyout-item.sb-flyout-active { background: rgba(255,255,255,0.09); color: #c8e94b; }

/* Collapsed tooltip (left is set inline from COLLAPSED_W) */
.sb-tip {
  position: fixed; transform: translateY(-50%); z-index: 9999;
  background: #0a322d; color: #fff; font-size: 0.76rem; font-weight: 500;
  padding: 5.12px 11.27px; border-radius: 7.17px; white-space: nowrap; pointer-events: none;
  border: 1px solid rgba(255,255,255,0.14);
  box-shadow: 0 6px 18px rgba(0,0,0,0.25);
}

.sb-root nav::-webkit-scrollbar { width: 4px; }
.sb-root nav::-webkit-scrollbar-track { background: transparent; }
.sb-root nav::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.18); border-radius: 10px; }
`;

export default Sidebar;